import React, { createContext, ReactNode, useState, useEffect, useRef } from 'react';

import { ChatContextType } from './types/chatContextType';
import { ChatMessage, ChatOneToOneConversation } from '../data_models/chat';
import { UserId } from '../data_models/helpers';

import { useSocket } from '../hooks/useSocket';
import useCurrentUser from '../hooks/useCurrentUser';
import { IFreelancer } from '../data_models/freelancer';
import { IEmployer } from '../data_models/employer';
// import { Invitation } from '../data_models/invitation';
import { getConverstationIdForInvitation, getNumberUnreadMessagesInConversation, getSortedConversations, getSortedListOfMessages, removeDuplicatesFromMessages } from '../utils/chatUtils';
import useChatHelper from './helper/useChatHelper';
import { EUserType } from '../data_models/enums';
import useUser from '../hooks/useUser';
import { Invitation } from '../data_models/invitation';
// import useInvitations from '../hooks/useInvitations';

// Create the context
export const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider component
// KNOWN BUG - callback function is executed multiple times after receiving a message on socket
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {


    const { currentUser, currentUserType } = useCurrentUser();
    const { fetchBriefFreelancersByIds, fetchBriefEmployerByIds } = useUser();
    const { fetchAllPrivateConversations,
        fetchPrivateConversation,
        fetchPrivateMessages,
        fetchConversationByPartner,
        fetchPartnersByConversationId,
        sendPrivateMessage: _sendPrivateMessage,
        sendPrivateMessageFile: _sendPrivateMessageFile,
        downloadPrivateMessageFile: _downloadPrivateMessageFile,
        markPrivateMessagesAsRead: _markPrivateMessagesAsRead,
        notifyTyping: _notifyTyping,
    } = useChatHelper();
    // const {
    //     fetchInvitationsForUser,
    //     fetchInvitationsFromUser,
    //     markInvitationAsSeenByFreelancer: _markInvitationAsSeenByFreelancer,
    // } = useInvitations();

    const { socket, listenToMessages, listenToInvitations, listenToOneToOneConversations, listenToTyping } = useSocket();

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [oneToOneConversations, setOneToOneConversations] = useState<ChatOneToOneConversation[] | undefined>();
    const [messagesByConversation, setMessagesByConversation] = useState<Map<string, ChatMessage[]> | undefined>();
    const [chatPartners, setChatPartners] = useState<Map<string, IFreelancer | IEmployer>>();
    // const [invitations, setInvitations] = useState<Invitation[]>();

    // active conversation data
    const [startingNewConversation, setStartingNewConversation] = useState<boolean>(false);
    const [activeConversation, setActiveConversation] = useState<ChatOneToOneConversation | undefined>();
    const [activeMessages, setActiveMessages] = useState<ChatMessage[] | undefined>();
    const [activeChatHasOlderMessages, setActiveChatHasOlderMessages] = useState<boolean>(false);
    const [activeChatPartner, setActiveChatPartner] = useState<IFreelancer | IEmployer | undefined>();

    // for notifications
    const [conversationsWithUnreadMessages, setConversationsWithUnreadMessages] = useState<Map<string, number>>(new Map<string, number>()); // ids of conversations with unread messages and number of unread messages
    const [totalNumberUnreadMessages, setTotalNumberUnreadMessages] = useState<number>(0);

    // for typing (conversationId -> [senderId])
    const [typing, setTyping] = useState<Map<string, UserId[]>>(new Map<string, UserId[]>()); // ids of conversations with unread messages and number of unread messages

    // pagination variables
    const [chatPage, setChatPage] = useState(1);
    const [chatLimit, setChatLimit] = useState(50);
    const [conversationPage, setConversationPage] = useState(1);
    const [conversationLimit, setConversationLimit] = useState(1000);
    const [invitationPage, setInvitationPage] = useState(1);
    const [invitationLimit, setInvitationLimit] = useState(1000);


    // useRef needed for proper callback execution
    const oneToOneConversationsRef = useRef(oneToOneConversations);
    const messagesByConversationRef = useRef(messagesByConversation);
    const activeConversationRef = useRef(activeConversation);
    const activeMessagesRef = useRef(activeMessages);
    const chatPartnersRef = useRef(chatPartners);
    const conversationsWithUnreadMessagesRef = useRef(conversationsWithUnreadMessages);
    const typingRef = useRef(typing);
    // needed to wrap the bug that callback is executed multiple times. ensure each message is 
    // added only one time and is updated if newer
    const idMapMessages = useRef(new Map<string, Date>()); // id and last updated date
    const idMapInvitations = useRef(new Map<string, Date>()); // id and last updated date
    const idMapConversations = useRef(new Map<string, Date>()); // id and last updated date

    // Map to store timeouts for each user in each conversation
    const typingTimeoutsRef = useRef<Map<string, Map<string, NodeJS.Timeout>>>(new Map());

    // TODO bug - as duplicated message ids are ignored - existing messages will 
    // not be updated through socket. e.g. read notification or so
    const receivePrivateMessageCallback = async (message: ChatMessage) => {

        if (!message._id) {
            return;
        }

        // check if message exists and if so, check if it is newer
        if (idMapMessages.current.has(message._id)) {
            const someEarlyDate = new Date('1900-01-01');
            const currMessageDate = new Date(idMapMessages.current.get(message._id) || someEarlyDate);
            const newMessageDate = new Date(message._updatedAt!);
            if (currMessageDate < newMessageDate) {
                idMapMessages.current.set(message._id, message._updatedAt!);
            } else {
                return;
            }
        } else {
            idMapMessages.current.set(message._id, message._updatedAt!);

        }


        // check if conversation is loaded, otherwise fetch
        // (if another person messages a user for the first time, there is no local conversation)
        if (!oneToOneConversationsRef.current?.find(c => c._id === message.conversationId)) {
            return; // this case is handled by receiveConversationCallback
            // await fetchPrivateConversationAndSort(message.conversationId);
            // // fetch and set respective new chat partner
            // console.log('message - fetching new chat partner...', message.conversationId)
            // await fetchNewChatpartner(message.conversationId);
        }

        // add message to the messages by conversation map
        addOrUpdateMessageToConversationsMap(message.conversationId, message);

        // if conversation is the currently active - also add it to active messages
        checkAndAddOrUpdateToActiveMessages(message.conversationId, message);

        // move position to first position as it has the most recent message
        moveConversationToFirstPosition(message.conversationId);

        // update unread messages data
        updateUnreadMessagesData(message.conversationId, message);

        // if currently active, mark as read
        if (activeConversationRef.current?._id === message.conversationId) {
            markMessagesAsRead([message._id], message.conversationId);
        }

        // if no conversation is currently active, set to active
        if (!activeConversationRef.current) {
            await setFirstConversationActive();
        }
    };


    const receivePrivateConversationCallback = async (conversation: ChatOneToOneConversation) => {
        if (!conversation._id) {
            return;
        }

        // check if conversation exists and if so, check if it is newer
        if (idMapConversations.current.has(conversation._id)) {
            const someEarlyDate = new Date('1900-01-01');
            const currConversationDate = new Date(idMapConversations.current.get(conversation._id) || someEarlyDate);
            const newConversationDate = new Date(conversation._updatedAt!);
            if (currConversationDate < newConversationDate) {
                idMapConversations.current.set(conversation._id, conversation._updatedAt!);
            } else {
                return;
            }
        } else {
            idMapConversations.current.set(conversation._id, conversation._updatedAt!);
        }

        // check if only conversation object should be updated or also its messages
        // conversation 
        // - if new updated date is newer than existing
        // 
        // update messages if:
        // - active conversation start (active partner is defined, active messages are undefined)
        // - passive conversation start (conversation does not exist, chat partner does not exist)
        //
        // (otherwise messages are updated through receivePrivateMessageCallback)


        // Conversations
        // Case 1: conversation already exists -> update conversation object
        let updateMessages = false;
        if (oneToOneConversationsRef.current?.find(c => c._id === conversation._id)) {
            const newConversations = oneToOneConversationsRef.current?.map(c => {
                if (c._id === conversation._id) {
                    return conversation;
                }
                return c;
            });
            setOneToOneConversations(newConversations);
        }
        // Case 2: conversation does not exist -> add new conversation
        else {
            const newConversations = [...(oneToOneConversationsRef.current || []), conversation];
            setOneToOneConversations(getSortedConversations(newConversations));
            oneToOneConversationsRef.current = getSortedConversations(newConversations);
            updateMessages = true;
        }

        // Messages 
        // Check if active start of conversation
        if (activeChatPartner && !activeMessages) {
            updateMessages = true;
            await fetchNewChatpartner(conversation._id!, activeChatPartner);
        }

        if (updateMessages) {
            // fetch messages and invitations for new conversation
            const result = await fetchPrivateMessages(conversation._id!, chatPage, chatLimit);
            setActiveChatHasOlderMessages(result.totalPages > chatPage);

            const messages = result.results;
            // update unread messages data
            for (const message of messages) {
                updateUnreadMessagesData(conversation._id!, message);
            }
            setActiveMessages(messages);
        }

        // if no conversation is currently active, set to active
        if (!activeConversationRef.current) {
            setFirstConversationActive();
        }
    };

    const receiveTypingCallback = async (sender: UserId, conversationId: string) => {
        const newTyping = new Map(typingRef.current);
        const currentTypingUsers = newTyping.get(conversationId) || [];
        const userKey = `${sender.id}-${sender.userType}`;

        console.log('receiveTypingCallback', sender, conversationId);

        // Clear any existing timeout for this user in this conversation
        const conversationTimeouts = typingTimeoutsRef.current.get(conversationId) || new Map();
        const existingTimeout = conversationTimeouts.get(userKey);
        if (existingTimeout) {
            clearTimeout(existingTimeout);
        }

        // Add the new typing user if not already present
        if (!currentTypingUsers.some(user => user.id === sender.id && user.userType === sender.userType)) {
            newTyping.set(conversationId, [...currentTypingUsers, sender]);
            setTyping(newTyping);
            typingRef.current = newTyping;
        }

        // Set new timeout to remove the user after 7 seconds
        const timeout = setTimeout(() => {
            const updatedTyping = new Map(typingRef.current);
            const updatedUsers = (updatedTyping.get(conversationId) || []).filter(
                user => !(user.id === sender.id && user.userType === sender.userType)
            );

            if (updatedUsers.length === 0) {
                updatedTyping.delete(conversationId);
            } else {
                updatedTyping.set(conversationId, updatedUsers);
            }

            setTyping(updatedTyping);
            typingRef.current = updatedTyping;

            // Clean up the timeout reference
            const updatedTimeouts = typingTimeoutsRef.current.get(conversationId);
            if (updatedTimeouts) {
                updatedTimeouts.delete(userKey);
                if (updatedTimeouts.size === 0) {
                    typingTimeoutsRef.current.delete(conversationId);
                }
            }
        }, 7000);

        // Store the new timeout
        if (!typingTimeoutsRef.current.has(conversationId)) {
            typingTimeoutsRef.current.set(conversationId, new Map());
        }
        typingTimeoutsRef.current.get(conversationId)?.set(userKey, timeout);
    };

    // fetch and set new chat partner for a given conversationId
    // can use an already fetched user which is helpful when a new conversation is actively started and the user is already fetched
    const fetchNewChatpartner = async (conversationId: string, useAlredadyFetchedUser?: IFreelancer | IEmployer) => {
        let convChatPartner: IEmployer | IFreelancer | undefined;
        if (useAlredadyFetchedUser) {
            convChatPartner = useAlredadyFetchedUser;
        } else {
            const convChatPartners = await fetchPartnersByConversationId(conversationId);
            convChatPartner = convChatPartners.employers.length > 0
                ? convChatPartners.employers[0]
                : convChatPartners.freelancers[0];
        }

        if (convChatPartner) {
            const newChatPartners: Map<string, IFreelancer | IEmployer> = new Map(chatPartnersRef.current);
            newChatPartners.set(conversationId, convChatPartner);
            setChatPartners(newChatPartners);
            chatPartnersRef.current = newChatPartners;
        }
    }

    // add message to the messages by conversation map 
    const addOrUpdateMessageToConversationsMap = (conversationId: string, msg: ChatMessage) => {
        // 1. if message / invitation already exists, update with new version
        // 2. else add as new message        
        // manipulate messages
        const messages = messagesByConversationRef.current?.get(conversationId) || [];
        let updated = false;
        messages.forEach((m, idx) => {
            if (m._id === msg._id) {
                messages[idx] = msg;
                updated = true;
            }
        });

        if (!updated) {
            const updatedTyping = new Map(typingRef.current);
            updatedTyping.delete(msg.conversationId);
            setTyping(updatedTyping);

            messages.push(msg);
        }

        // update the data
        const newMessagesMap = !messagesByConversationRef.current
            ? new Map<string, ChatMessage[]>()
            : new Map(messagesByConversationRef.current);

        newMessagesMap.set(conversationId, messages);

        setMessagesByConversation((prev) => { return newMessagesMap });
    }


    // Checks if the conversation is currently active and if so - add/update the message to active messages
    const checkAndAddOrUpdateToActiveMessages = (conversationId: string, msg: ChatMessage) => {
        // Check if the conversation is currently active
        if (conversationId && activeConversationRef.current?._id === conversationId) {
            const updatedMsgs: ChatMessage[] = [];

            // if starting a new conversation - just push message
            if (activeMessagesRef.current === undefined) {
                updatedMsgs.push(msg);
            } else {
                // check if this will update an existing message or will add a new one
                let update = false;
                for (const message of activeMessagesRef.current!) {
                    if (message._id === msg._id) {
                        updatedMsgs.push(msg);
                        update = true;
                    } else {
                        updatedMsgs.push(message);
                    }
                }
                // in case it wasnt updated, add msg to end
                if (!update) {
                    updatedMsgs.push(msg);
                }
            }

            // Update active messages
            setActiveMessages((prevMessages) => {
                return updatedMsgs;
            });
        }
    }

    const moveConversationToFirstPosition = (conversationId: string) => {
        const conversation = oneToOneConversationsRef.current?.find(c => c._id === conversationId);
        if (conversation) {
            const newConversations = oneToOneConversationsRef.current?.filter(c => c._id !== conversationId) || [];
            newConversations.unshift(conversation);
            setOneToOneConversations((prev) => { return newConversations; });
        }
    }

    // check if conversations exist, and if so sets the first existing 
    // conversation active and fetches its corresponding chat partner
    const setFirstConversationActive = async () => {
        if (oneToOneConversationsRef.current?.length! > 0) {
            unselectActiveChat();
            setActiveConversation(oneToOneConversationsRef.current![0]);
            await fetchNewChatpartner(oneToOneConversationsRef.current![0]._id!);
            const chatPartner = chatPartnersRef.current?.get(oneToOneConversationsRef.current![0]._id!);
            setActiveChatPartner(chatPartner);
            setStartingNewConversation(false);
        }
    }

    // Cleanup function to clear all timeouts when component unmounts
    useEffect(() => {
        return () => {
            typingTimeoutsRef.current.forEach(conversationTimeouts => {
                conversationTimeouts.forEach(timeout => clearTimeout(timeout));
            });
            typingTimeoutsRef.current.clear();
        };
    }, []);

    useEffect(() => {
        messagesByConversationRef.current = messagesByConversation;
        activeConversationRef.current = activeConversation;
        activeMessagesRef.current = activeMessages;
        oneToOneConversationsRef.current = oneToOneConversations;
        chatPartnersRef.current = chatPartners;
        conversationsWithUnreadMessagesRef.current = conversationsWithUnreadMessages;
        typingRef.current = typing;
    }, [
        messagesByConversation,
        activeConversation,
        activeMessages,
        oneToOneConversations,
        chatPartners,
        conversationsWithUnreadMessages,
        typing
    ]);

    useEffect(() => {
        if (activeMessages) {
            // find all unread messages and mark as read
            const unreadMsgIds: string[] = [];
            for (let i = activeMessagesRef.current?.length! - 1; i >= 0; i--) {
                const msg = activeMessagesRef.current![i];
                if (msg.sender.userType !== currentUserType ||
                    msg.sender.id !== currentUser?._id
                ) { // if ChatMessage
                    let read = false
                    for (let j = 0; j < msg.readByUsersAt.length; j++) {
                        if (msg.readByUsersAt[j].userId.id === currentUser?._id &&
                            msg.readByUsersAt[j].userId.userType === currentUserType) {
                            read = true;
                            break;
                        }
                    }
                    // if (read) break; // break after first found read message. everything from there will be read anyway.
                    unreadMsgIds.push(msg._id!);
                }
            }

            // no need to await. no result. updated data will come via sockets
            if (unreadMsgIds.length > 0) {
                markMessagesAsRead(unreadMsgIds, activeConversationRef.current?._id!);
            }


        }
    }, [activeMessages])

    useEffect(() => {
        try {
            if (currentUser && currentUserType) {
                listenToMessages(receivePrivateMessageCallback);
                listenToOneToOneConversations(receivePrivateConversationCallback);
                listenToTyping(receiveTypingCallback);

                // Cleanup function to remove the listener
                return () => {
                    socket?.off("receive_private_message", receivePrivateMessageCallback);
                    socket?.off("receive_private_message_conversation", receivePrivateConversationCallback);
                };
            }
        } catch (error) {
            console.error("ChatProvider", error)
        }
    }, [
        currentUser,
        currentUserType,
        listenToMessages,
        listenToOneToOneConversations,

        socket,
    ]);

    useEffect(() => {
        // user must be logged in otherwise server requests are not valid
        if (!currentUser || !currentUserType) return;

        setLoading(true);
        // private conversations
        fetchAllPrivateConversations(conversationPage, conversationLimit).then(async (conversations) => {
            if (!conversations) return;

            // set the conversation sorted by last message
            setOneToOneConversations(getSortedConversations(conversations) || []);

            // fetch chat partners
            const freelancerIds: string[] = [];
            const employerIds: string[] = [];
            let totalUnread = 0;
            const conversationsWithUnreadMessages = new Map<string, number>();
            conversations?.forEach((conversation) => {
                for (const uid of conversation.participants) {
                    if (uid.id !== currentUser?._id || uid.userType !== currentUserType) {
                        if (uid.userType === EUserType.FREELANCER) {
                            freelancerIds.push(uid.id);
                        } else if (uid.userType === EUserType.EMPLOYER) {
                            employerIds.push(uid.id);
                        }
                    }
                }
                const unread = getNumberUnreadMessagesInConversation(
                    conversation,
                    { id: currentUser?._id!, userType: currentUserType } as UserId
                ) || 0;
                totalUnread += unread;
                conversationsWithUnreadMessages.set(conversation._id!, unread);
            });
            setTotalNumberUnreadMessages(totalUnread);
            setConversationsWithUnreadMessages(conversationsWithUnreadMessages);

            const employers = await fetchBriefEmployerByIds(employerIds) || [];
            const freelancers = await fetchBriefFreelancersByIds(freelancerIds) || [];

            // set partners with correct conversation id
            const newChatPartners = new Map(chatPartners);
            conversations?.forEach((conversation) => {
                let partnerUid: UserId | undefined;
                conversation.participants.forEach((participant) => {
                    if (participant.id !== currentUser?._id || participant.userType !== currentUserType) {
                        partnerUid = participant;
                    }
                })

                if (partnerUid) {
                    let partner;
                    if (partnerUid.userType === EUserType.FREELANCER) {
                        partner = freelancers.find((freelancer) => freelancer._id === partnerUid!.id);
                    } else {
                        partner = employers.find((employer) => employer._id === partnerUid!.id);
                    }
                    if (partner) {
                        newChatPartners.set(conversation._id!, partner);
                    }
                }
            })
            setChatPartners(newChatPartners);

            // update total unread messages data
            // total number of unread messages
            let totalUnreadMessages = 0;
            conversationsWithUnreadMessagesRef.current?.forEach((value, key) => {
                totalUnreadMessages += value;
            });
            setTotalNumberUnreadMessages(totalUnreadMessages);
        })
            .catch((error) => { console.error(error) })
            .finally(() => {
                setLoading(false);
            });
    }, [currentUser,
        currentUserType]);


    const setActiveConversationByPartner = async (partner: IFreelancer | IEmployer | undefined) => {

        setLoading(true);
        unselectActiveChat();
        if (partner) {
            // set new chat partner
            setActiveChatPartner(partner);

            // find conversation based on partner id
            const partnerType = 'employerName' in partner ? EUserType.EMPLOYER : EUserType.FREELANCER;
            let conversation = oneToOneConversations?.find(c => c.participants.find(p => p.id === partner._id && p.userType === partnerType));

            try {
                if (!conversation) {
                    const partnerUserId: UserId = { id: partner._id!, userType: partnerType };
                    conversation = await fetchConversationByPartner(partnerUserId);
                }
            } catch (error: any) {
                setActiveMessages(undefined);
                // if converstion does not exist early return (no prev conversation between users)
                return;
            }

            let result;
            if (conversation) {
                result = await fetchPrivateMessages(conversation?._id!, chatPage, chatLimit);
                setActiveChatHasOlderMessages(result?.totalPages > chatPage);
            }
            let messages = result?.results;
            if (messages) {
                messages = getSortedListOfMessages(messages);
            }

            const newMessagesMap = new Map(messagesByConversation);
            newMessagesMap.set(conversation?._id!, []);
            setMessagesByConversation(newMessagesMap);

            setActiveConversation(conversation);
            setActiveMessages(messages);
        }
        setLoading(false);
    }

    const getConversationIdByPartnerId = (partnerId: string) => {
        return oneToOneConversations?.find(c => c.participants.find(p => p.id === partnerId))?._id;
    }

    const sendPrivateMessage = async (partnerId: UserId, content: string): Promise<ChatMessage> => {
        return _sendPrivateMessage(partnerId, content);
    }

    const sendPrivateMessageFile = async (partnerId: UserId, file: File): Promise<ChatMessage> => {
        return _sendPrivateMessageFile(partnerId, file);
    }

    const downloadPrivateMessageFile = async (metadataId: string, conversationId: string, filename: string, targetLocation: string) => {
        return _downloadPrivateMessageFile(metadataId, conversationId, filename, targetLocation);
    }

    // update unread messages data
    const updateUnreadMessagesData = (conversationId: string, message: ChatMessage) => {
        // if message is from current user, do nothing

        if (message.sender.id === currentUser?._id && message.sender.userType === currentUserType) {
            return;
        }

        // if message is to current user, update unread messages data
        const newConversationsWithUnreadMessages = new Map(conversationsWithUnreadMessagesRef.current);
        const currUnread = conversationsWithUnreadMessagesRef.current.get(conversationId) || 0;
        newConversationsWithUnreadMessages.set(conversationId, currUnread + 1);
        setConversationsWithUnreadMessages(newConversationsWithUnreadMessages);

        setTotalNumberUnreadMessages(prev => prev + 1);
    }


    const markMessagesAsRead = async (messageIds: string[], conversationId: string) => {
        await _markPrivateMessagesAsRead(messageIds);

        // update total number of unread messages
        let totalUnread = Math.max(totalNumberUnreadMessages - messageIds.length, 0);
        setTotalNumberUnreadMessages(totalUnread);

        // update number of unread messages in conversation
        const conversationWithUnreadMessages = new Map(conversationsWithUnreadMessagesRef.current);
        if (conversationWithUnreadMessages.has(conversationId)
            && (conversationWithUnreadMessages.get(conversationId) || 0) - messageIds.length <= 0) {
            conversationWithUnreadMessages.delete(conversationId);
        } else {
            conversationWithUnreadMessages.set(conversationId, conversationWithUnreadMessages.get(conversationId)! - messageIds.length);
        }
        setConversationsWithUnreadMessages(conversationWithUnreadMessages);
    }

    const fetchOlderMessages = async () => {
        const page = chatPage + 1;
        setChatPage(page);

        let result = await fetchPrivateMessages(activeConversationRef.current?._id!, page, chatLimit);
        setActiveChatHasOlderMessages(result.totalPages > chatPage);

        let messages = result.results;
        messages = getSortedListOfMessages(messages);

        const newMessagesMap = new Map(messagesByConversation);
        const currMessages = newMessagesMap.get(activeConversationRef.current?._id!) || [];

        let combinedMessages = [...messages, ...currMessages];
        combinedMessages = removeDuplicatesFromMessages(combinedMessages);
        newMessagesMap.set(activeConversationRef.current?._id!, combinedMessages);

        setMessagesByConversation(newMessagesMap);
        setActiveMessages(combinedMessages);
    }

    const unselectActiveChat = () => {
        setChatPage(1);
        setActiveConversation(undefined);
        setActiveMessages(undefined);
        setActiveChatPartner(undefined);
    }

    const notifyTyping = async () => {
        const activeConversationId = activeConversation?._id;
        if (!activeConversationId) return;
        await _notifyTyping(activeConversationId);
    }



    return (
        <ChatContext.Provider value={{
            error,
            loading,
            oneToOneConversations,
            messagesByConversation,
            chatPartners,

            activeConversation,
            activeMessages,
            activeChatPartner,
            activeChatHasOlderMessages,
            conversationsWithUnreadMessages,
            totalNumberUnreadMessages,
            typing,
            getConversationIdByPartnerId,

            fetchOlderMessages,
            setFirstConversationActive,
            setActiveConversationByPartner,
            sendPrivateMessage,
            sendPrivateMessageFile,
            downloadPrivateMessageFile,
            unselectActiveChat,
            notifyTyping,
        }}>
            {children}
        </ChatContext.Provider>
    );
};

